<template>
  <form @submit.prevent="saveObservation" ref="observationForm">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="observationModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">{{ action === 'edit' ? $t('observationModal.titleEdit') : $t('observationModal.titleAdd') }}</h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>
              <div class="form-group">
                <label>{{ $t('observationModal.pointLabel') }}</label>
                <div class="blue-text">{{ seriesLabel }}</div>
                <div class="small text-muted" v-if="form.value != null">
                  {{ formatNumber(form.value) }} {{ unit }}<span v-if="previousValue != null"> ({{ $t('observationModal.previousValue') }} {{ formatNumber(previousValue) }} {{ unit }})</span>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('observationModal.timeInput') }}</label>
                <VueDatePicker v-if="action !== 'edit'" v-model="form.observedat" model-type="iso" :enable-time-picker="true" :auto-apply="true" />
                <div v-else class="form-control-plaintext">{{ timeDisplay }}</div>
              </div>
              <div class="form-group">
                <label for="observationDeltaInput">{{ $t('observationModal.deltaInput') }}<span v-if="unit">&nbsp;/&nbsp;{{ unit }}</span></label>
                <div class="input-row">
                  <input type="text" inputmode="decimal" class="form-control" id="observationDeltaInput" v-model.trim="form.delta" :class="{ 'is-invalid': v$.form.delta.$dirty && v$.form.delta.$error }" />
                  <span v-if="proposalLoading" class="spinner-border spinner-border-sm"></span>
                </div>
                <div class="invalid-feedback" style="display: block;" v-if="v$.form.delta.$dirty && v$.form.delta.$error">
                  {{ $t('observationModal.deltaInvalid') }}
                </div>
              </div>
              <div class="form-group">
                <label for="observationKindSelect">{{ $t('observationModal.kindInput') }}</label>
                <select class="form-control" id="observationKindSelect" v-model.number="form.kind">
                  <option v-for="k in kinds" :value="k" :key="k">{{ $t('observationKind.' + k) }}</option>
                </select>
              </div>
              <div class="form-group">
                <label for="observationApplianceSelect">{{ $t('observationModal.applianceInput') }}</label>
                <select class="form-control" id="observationApplianceSelect" v-model="form.applianceid" @change="applianceChanged">
                  <option value="">{{ $t('observationModal.applianceNone') }}</option>
                  <option v-for="a in appliances" :value="String(a.id)" :key="a.id">{{ a.name }}</option>
                  <option value="__new__">{{ $t('observationModal.applianceNew') }}</option>
                </select>
                <div class="invalid-feedback" style="display: block;" v-if="showApplianceRequired">
                  {{ $t('observationModal.applianceRequired') }}
                </div>
              </div>
              <div class="form-group">
                <label for="observationNoteInput">{{ $t('observationModal.noteInput') }}</label>
                <input type="text" class="form-control" id="observationNoteInput" v-model.trim="form.note" maxlength="250" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary">{{ action === 'edit' ? $t('observationModal.buttonSave') : $t('observationModal.buttonAdd') }}</button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('observationModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import moment from 'moment'
import { useVuelidate } from '@vuelidate/core'
import observationsService from '@/services/observations'
import { isDecimalInput, parseDecimalInput } from '@/utils/decimal-input.js'
import { KIND_ON, KIND_OFF, KIND_OTHER, defaultKind } from '@/utils/chart-observations'

//
// Beobachtung an einem Chart-Punkt: Ein / Aus / Sonstiges, Delta (vom
// Chart vorgeschlagen, editierbar), optionaler Verbraucher, Notiz.
// Bei "add" kommt der Vorschlag aus dem Chart-Klick (utils/chart-
// observations proposeObservation); fehlt dort der Vorpunkt, fragt der
// Modal den Server (/observations/propose). Bei "edit" sind Sensor,
// Serie und Zeit unveraenderlich.
//
export default {
  setup () { return { v$: useVuelidate() } },
  name: 'ObservationModal',
  props: {
    currentSensor: { type: Object, required: true },
    // appliances offered in the picker (parent pre-filters by category / point)
    appliances: { type: Array, default: () => [] },
    // { systempart, valuetype, observedat (iso), value, previousValue, delta, kind, applianceid? }
    proposal: { type: Object, default: null },
    // existing observation (wire shape) when editing
    observation: { type: Object, default: null },
    action: { type: String, default: 'add' },
    unit: { type: String, default: '' },
    modalOpened: { type: String, required: true }
  },
  emits: ['modalClosed', 'createAppliance'],
  data () {
    return {
      form: this.emptyForm(),
      previousValue: null,
      kinds: [KIND_ON, KIND_OFF, KIND_OTHER],
      proposalLoading: false,
      showApplianceRequired: false,
      errorMessage: '',
      anyChanges: false
    }
  },
  validations: {
    form: {
      delta: { decimal: isDecimalInput }
    }
  },
  computed: {
    seriesLabel () {
      const sp = this.form.systempart
      const vt = this.form.valuetype
      if (sp == null || vt == null) return ''
      return this.$t('systemPart.' + sp) + ' ' + this.$t('valueType.' + vt)
    },
    timeDisplay () {
      return this.form.observedat ? moment(this.form.observedat).format('DD.MM.YYYY HH:mm') : ''
    }
  },
  watch: {
    modalOpened () {
      this.resetForm()
    }
  },
  methods: {
    emptyForm () {
      return {
        systempart: null,
        valuetype: null,
        observedat: null,
        value: null,
        delta: '',
        kind: KIND_OTHER,
        applianceid: '',
        note: ''
      }
    },
    resetForm () {
      this.errorMessage = ''
      this.anyChanges = false
      this.showApplianceRequired = false
      this.proposalLoading = false
      this.previousValue = null
      if (this.action === 'edit' && this.observation) {
        const o = this.observation
        this.form = {
          systempart: o.systempart,
          valuetype: o.valuetype,
          observedat: o.observedat,
          value: o.value != null ? Number(o.value) : null,
          delta: o.delta != null ? String(o.delta).replace('.', ',') : '',
          kind: o.kind != null ? Number(o.kind) : KIND_OTHER,
          applianceid: o.applianceid != null ? String(o.applianceid) : '',
          note: o.note || ''
        }
      } else if (this.proposal) {
        const p = this.proposal
        this.form = {
          systempart: p.systempart,
          valuetype: p.valuetype,
          observedat: p.observedat,
          value: p.value != null ? p.value : null,
          delta: p.delta != null ? this.deltaString(p.delta) : '',
          kind: p.kind != null ? p.kind : defaultKind(p.delta),
          applianceid: p.applianceid != null ? String(p.applianceid) : '',
          note: p.note || ''
        }
        this.previousValue = p.previousValue != null ? p.previousValue : null
        if (p.delta == null && p.observedat && this.currentSensor && this.currentSensor.id >= 0) {
          this.fetchProposal()
        }
      } else {
        this.form = this.emptyForm()
      }
      this.v$.$reset()
    },
    deltaString (delta) {
      const rounded = Math.round(Number(delta) * 1000) / 1000
      return String(rounded).replace('.', ',')
    },
    formatNumber (value) {
      if (value == null || isNaN(value)) return '-'
      return Number(value).toLocaleString('de-DE', { maximumFractionDigits: 3 })
    },
    // no previous point in the chart window: ask the server for the two
    // readings around the timestamp
    fetchProposal () {
      this.proposalLoading = true
      const at = moment(this.form.observedat).valueOf()
      observationsService.propose(this.currentSensor.id, this.form.systempart, this.form.valuetype, at).then((data) => {
        const p = data && data.proposal
        if (!p) return
        if (p.previous && p.previous.scaledValue != null) this.previousValue = Number(p.previous.scaledValue)
        if (this.form.value == null && p.current && p.current.scaledValue != null) this.form.value = Number(p.current.scaledValue)
        if (p.delta != null && this.form.delta === '') {
          this.form.delta = this.deltaString(p.delta)
          this.form.kind = defaultKind(Number(p.delta))
        }
      }).catch(() => {
        // proposal is a convenience — the user can still type the delta
      }).finally(() => { this.proposalLoading = false })
    },
    applianceChanged () {
      if (this.form.applianceid === '__new__') {
        this.form.applianceid = ''
        this.$emit('createAppliance', { systempart: this.form.systempart })
        this.hide()
      }
    },
    saveObservation () {
      this.v$.$touch()
      const needsAppliance = this.form.kind === KIND_ON || this.form.kind === KIND_OFF
      this.showApplianceRequired = needsAppliance && !this.form.applianceid
      if (this.v$.$invalid || this.showApplianceRequired || !this.form.observedat) {
        return
      }
      const delta = parseDecimalInput(this.form.delta)
      const applianceid = this.form.applianceid ? Number(this.form.applianceid) : null
      let request
      if (this.action === 'edit' && this.observation) {
        request = observationsService.patchObservation(this.observation.id, {
          kind: this.form.kind,
          delta,
          applianceid: applianceid != null ? applianceid : -1,
          note: this.form.note || ''
        })
      } else {
        request = observationsService.addObservation(this.currentSensor.id, {
          systempart: this.form.systempart,
          valuetype: this.form.valuetype,
          observedat: moment(this.form.observedat).valueOf(),
          kind: this.form.kind,
          delta,
          value: this.form.value,
          applianceid,
          note: this.form.note || ''
        })
      }
      request.then(() => {
        this.anyChanges = true
        this.close()
      }).catch(error => {
        this.errorMessage = this.localizedBackendMessage(error.message)
      })
    },
    onEsc () {
      this.close()
    },
    hide () {
      Modal.getInstance(document.getElementById('observationModal'))?.hide()
    },
    close () {
      this.$emit('modalClosed', this.anyChanges)
      this.hide()
    },
    localizedBackendMessage (errorMessage) {
      return this.$t('backend.' + errorMessage)
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  .modal-dialog {
    width: 480px;
  }
  .blue-text {
    color: #0069d9;
    font-weight: 700;
  }
  .input-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}
</style>
